import {
    Alert,
    Badge,
    Button,
    Group,
    NumberInput,
    Paper,
    Select,
    Stack,
    Table,
    Text
} from "@mantine/core";
import classes from "../ProductionModulePlannerPage.module.css";
import {
    BlockMaterialsSummary,
    BlockMetrics,
    PlannerPlanStatusSummary,
    PlannerProductCatalogByType,
    PlannerStationCatalog
} from "../plannerTypes.ts";
import {
    buildEmptyPlanStatusSummary,
    buildLocationSelectorGroups,
    formatEtaWindow,
    formatNumber,
    getEntityTypeName,
    getLocationSupportError,
    getLocationWarningLevel,
    hasDatacardForProduct,
    parseIntInput,
    toSelectOptions
} from "../plannerUtils.ts";
import {
    ProductionGroup,
    ProductionPlannerBlock,
    ProductionPlannerProductType,
    ProductionTrackedEntity
} from "../types.ts";
import ProductionPlannerLocationGroupSelector from "./ProductionPlannerLocationGroupSelector.tsx";
import ProductionPlannerStatusBadges from "./ProductionPlannerStatusBadges.tsx";

type ProductionPlannerBlockCardProps = {
    block: ProductionPlannerBlock;
    blockIndex: number;
    entities: ProductionTrackedEntity[];
    moduleGroups: ProductionGroup[];
    metrics?: BlockMetrics;
    materialSummary: BlockMaterialsSummary;
    planStatuses: Map<string, PlannerPlanStatusSummary>;
    productCatalogByType: PlannerProductCatalogByType;
    stationTypeById: PlannerStationCatalog;
    validationErrors: string[];
    isMaterialsOpen: boolean;
    materialsReady: boolean;
    isLocationUsedElsewhere: (locationId: string) => boolean;
    onUpdate: (updater: (block: ProductionPlannerBlock) => ProductionPlannerBlock) => void;
    onOpenLocationIds: () => void;
    onRemove: () => void;
    onToggleMaterials: () => void;
};

const PRODUCT_TYPE_OPTIONS: Array<{value: ProductionPlannerProductType; label: string}> = [
    {value: "ship", label: "Ship"},
    {value: "vehicle", label: "Vehicle"},
    {value: "item", label: "Item"},
    {value: "droid", label: "Droid"},
];

export default function ProductionPlannerBlockCard({
    block,
    blockIndex,
    entities,
    moduleGroups,
    metrics,
    materialSummary,
    planStatuses,
    productCatalogByType,
    stationTypeById,
    validationErrors,
    isMaterialsOpen,
    materialsReady,
    isLocationUsedElsewhere,
    onUpdate,
    onOpenLocationIds,
    onRemove,
    onToggleMaterials,
}: ProductionPlannerBlockCardProps) {
    const selectedProductMap = productCatalogByType[block.productType];
    const productOptions = toSelectOptions(Array.from(selectedProductMap.values()));
    const selectedProduct = block.productId ? selectedProductMap.get(block.productId) ?? null : null;

    const compatibleLocations = entities
        .filter((entity) => !selectedProduct || !getLocationSupportError(entity, block.productType, selectedProduct, stationTypeById))
        .toSorted((a, b) => a.name.localeCompare(b.name));

    const locationOptionById = new Map(
        compatibleLocations.map((entity) => [
            entity.entityId,
            {
                id: entity.entityId,
                name: entity.name,
                typeName: getEntityTypeName(entity),
                disabled: isLocationUsedElsewhere(entity.entityId),
                warning: getLocationWarningLevel(entity),
                hasDatacard: hasDatacardForProduct(entity, block.productId),
            },
        ])
    );

    const locationGroups = buildLocationSelectorGroups(compatibleLocations, locationOptionById, moduleGroups);

    return (
        <Paper key={block.id ?? `block-${blockIndex}`} withBorder p="sm">
            <Stack gap="xs">
                <Group justify="space-between" align="center">
                    <Text fw={600}>Block #{blockIndex + 1}</Text>
                    <Group gap="xs">
                        <Button
                            size="compact-xs"
                            variant="light"
                            disabled={(block.locationEntityIds ?? []).length === 0}
                            onClick={onOpenLocationIds}
                        >
                            IDs
                        </Button>
                        <Button size="compact-xs" variant="light" color="red" onClick={onRemove}>
                            Remove
                        </Button>
                    </Group>
                </Group>

                <Group align="end">
                    <Select
                        className={classes.productTypeField}
                        label="Product type"
                        value={block.productType}
                        data={PRODUCT_TYPE_OPTIONS}
                        allowDeselect={false}
                        onChange={(value) => {
                            const nextType = (value as ProductionPlannerProductType) || "ship";
                            onUpdate((current) => ({
                                ...current,
                                productType: nextType,
                                productId: "",
                            }));
                        }}
                    />
                    <Select
                        className={classes.growInput}
                        searchable
                        label="Product"
                        value={block.productId}
                        data={productOptions}
                        onChange={(value) => {
                            onUpdate((current) => ({
                                ...current,
                                productId: value || "",
                            }));
                        }}
                    />
                </Group>

                <Group align="end">
                    <NumberInput
                        className={classes.quantityField}
                        label="Qty per location"
                        min={1}
                        max={48}
                        value={block.quantityPerLocation}
                        onChange={(value) => {
                            const next = parseIntInput(value, 1);
                            onUpdate((current) => ({
                                ...current,
                                quantityPerLocation: Math.max(1, Math.min(46, next)),
                            }));
                        }}
                    />
                    <NumberInput
                        className={classes.repeatField}
                        label="Repeat"
                        min={1}
                        value={block.repeat}
                        onChange={(value) => {
                            const next = parseIntInput(value, 1);
                            onUpdate((current) => ({
                                ...current,
                                repeat: Math.max(1, next),
                            }));
                        }}
                    />
                    <ProductionPlannerLocationGroupSelector
                        label="Production locations"
                        placeholder="Select locations"
                        className={classes.locationSelectorField}
                        nothingFoundMessage="No compatible locations"
                        groups={locationGroups}
                        optionById={locationOptionById}
                        value={block.locationEntityIds ?? []}
                        onChange={(value) => {
                            onUpdate((current) => ({
                                ...current,
                                locationEntityIds: value,
                            }));
                        }}
                    />
                </Group>

                {(metrics?.locations.length ?? 0) > 0 ? (
                    <div className={classes.locationTableWrap}>
                        <Table.ScrollContainer minWidth={200}>
                        <Table withTableBorder>
                            <Table.Thead>
                                <Table.Tr>
                                    <Table.Th>Location</Table.Th>
                                    <Table.Th>Type</Table.Th>
                                    <Table.Th>Status</Table.Th>
                                    <Table.Th>Plan Status</Table.Th>
                                    <Table.Th>Estimate</Table.Th>
                                </Table.Tr>
                            </Table.Thead>
                            <Table.Tbody>
                                {metrics?.locations.map((row) => {
                                    const planStatus = planStatuses.get(`${block.id ?? ""}:${row.locationId}`) ?? buildEmptyPlanStatusSummary();
                                    return (
                                        <Table.Tr key={`${block.id ?? blockIndex}-${row.locationId}`}>
                                            <Table.Td>{row.locationName}</Table.Td>
                                            <Table.Td>{row.locationTypeName}</Table.Td>
                                            <Table.Td><Text size="sm" c={row.statusColor}>{row.statusLabel}</Text></Table.Td>
                                            <Table.Td>
                                                <ProductionPlannerStatusBadges summary={planStatus} mode="compact"/>
                                            </Table.Td>
                                            <Table.Td>{row.estimateUnknown ? "Unknown" : row.estimateText}</Table.Td>
                                        </Table.Tr>
                                    );
                                })}
                            </Table.Tbody>
                        </Table>
                        </Table.ScrollContainer>
                    </div>
                ) : (
                    <Text size="sm" c="dimmed">No production locations selected.</Text>
                )}

                <Group gap="xs">
                    <Badge color="blue" variant="light">
                        Total units: {formatNumber(metrics?.totalUnits ?? 0)}
                    </Badge>
                    <Badge color="teal" variant="light">
                        ETA: {metrics?.etaUnknown ? "Unknown" : formatEtaWindow(metrics?.etaMin ?? null, metrics?.etaMax ?? null)}
                    </Badge>
                    <Badge color={materialSummary.missingTypes > 0 ? "red" : "green"} variant="light">
                        Materials: {materialSummary.missingTypes > 0 ? `${materialSummary.missingTypes} missing` : "ok"}
                    </Badge>
                    <Badge color="grape" variant="light">
                        Workers needed: {selectedProduct?.production?.workers ?? "Unknown"}
                    </Badge>
                </Group>

                {materialSummary.rows.length > 0 ? (
                    <>
                        <Group justify="space-between" align="center">
                            <Text size="sm" fw={600}>Block Material Requirements</Text>
                            <Button size="compact-xs" variant="subtle" onClick={onToggleMaterials}>
                                {isMaterialsOpen ? "Hide" : "Show"}
                            </Button>
                        </Group>
                        {isMaterialsOpen ? (
                            <Stack gap={6}>
                                <Text size="xs" c="dimmed">
                                    Available quantities are calculated from the selected block locations and shared access groups, not from total module inventory.
                                </Text>
                                {!materialsReady ? (
                                    <Alert color="yellow" title="Material inventory unavailable">
                                        Availability could not be loaded for this block yet.
                                    </Alert>
                                ) : null}
                                <div className={classes.locationTableWrap}>
                                    <Table withTableBorder>
                                        <Table.Thead>
                                            <Table.Tr>
                                                <Table.Th>Material</Table.Th>
                                                <Table.Th>Total Qty</Table.Th>
                                                <Table.Th>Available</Table.Th>
                                                <Table.Th>Missing</Table.Th>
                                            </Table.Tr>
                                        </Table.Thead>
                                        <Table.Tbody>
                                            {materialSummary.rows.map((row) => (
                                                <Table.Tr key={`${block.id ?? blockIndex}-${row.materialId}`}>
                                                    <Table.Td>{row.materialName}</Table.Td>
                                                    <Table.Td>{formatNumber(row.quantity)}</Table.Td>
                                                    <Table.Td>{row.available === null ? "-" : formatNumber(row.available)}</Table.Td>
                                                    <Table.Td>{row.missing === null ? "-" : formatNumber(row.missing)}</Table.Td>
                                                </Table.Tr>
                                            ))}
                                        </Table.Tbody>
                                    </Table>
                                </div>
                            </Stack>
                        ) : null}
                    </>
                ) : (
                    <Text size="sm" c="dimmed">No materials required for this block yet.</Text>
                )}

                {validationErrors.length > 0 ? (
                    <Alert color="red" title="Block validation">
                        {validationErrors.join(" | ")}
                    </Alert>
                ) : null}
            </Stack>
        </Paper>
    );
}
